import { memo, useState, useCallback, useEffect } from 'react';
import DocViewer, { DocViewerRenderers } from '@cyntler/react-doc-viewer';
import DocumentViewerSkeleton from './DocumentViewerSkeleton';

// Style par défaut (hors du composant pour éviter les re-renders)
const DEFAULT_STYLE = { height: '100%', maxHeight: 900, pdfVerticalScrollByDefault: true };

/**
 * Viewer d'images avec contrôles de zoom et pagination
 */
const ImageViewerWithZoom = memo(({ docs }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const currentDoc = docs[currentIndex];
  const totalDocs = docs.length;

  // Réinitialiser zoom et position lors du changement d'image
  useEffect(() => {
    setZoom(1);
    setPosition({ x: 0, y: 0 });
  }, [currentIndex]);

  const handlePrevious = useCallback(() => {
    setCurrentIndex(prev => (prev > 0 ? prev - 1 : totalDocs - 1));
  }, [totalDocs]);

  const handleNext = useCallback(() => {
    setCurrentIndex(prev => (prev < totalDocs - 1 ? prev + 1 : 0));
  }, [totalDocs]);

  const handleZoomIn = useCallback(() => {
    setZoom(prev => Math.min(prev + 0.25, 4));
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoom(prev => Math.max(prev - 0.25, 0.5));
  }, []);

  const handleReset = useCallback(() => {
    setZoom(1);
    setPosition({ x: 0, y: 0 });
  }, []);

  const handleMouseDown = useCallback((e) => {
    if (zoom > 1) {
      setIsDragging(true);
      setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
    }
  }, [zoom, position]);

  const handleMouseMove = useCallback((e) => {
    if (isDragging && zoom > 1) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    }
  }, [isDragging, dragStart, zoom]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleWheel = useCallback((e) => {
    e.preventDefault();
    if (e.deltaY < 0) {
      setZoom(prev => Math.min(prev + 0.1, 4));
    } else {
      setZoom(prev => Math.max(prev - 0.1, 0.5));
    }
  }, []);

  return (
    <div className="h-full flex flex-col">
      {/* Barre de contrôles */}
      <div className="flex items-center justify-between gap-2 p-2 bg-gray-100 border-b">
        {/* Pagination */}
        <div className="flex items-center gap-1">
          {totalDocs > 1 && (
            <>
              <button
                onClick={handlePrevious}
                className="p-2 rounded hover:bg-gray-200 transition-colors"
                title="Image précédente"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <span className="text-sm font-medium min-w-[60px] text-center">
                {currentIndex + 1} / {totalDocs}
              </span>
              <button
                onClick={handleNext}
                className="p-2 rounded hover:bg-gray-200 transition-colors"
                title="Image suivante"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </>
          )}
        </div>

        {/* Contrôles de zoom */}
        <div className="flex items-center gap-1">
          <button
            onClick={handleZoomOut}
            className="p-2 rounded hover:bg-gray-200 transition-colors"
            title="Zoom arrière"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7" />
            </svg>
          </button>
          <span className="text-sm font-medium min-w-[50px] text-center">
            {Math.round(zoom * 100)}%
          </span>
          <button
            onClick={handleZoomIn}
            className="p-2 rounded hover:bg-gray-200 transition-colors"
            title="Zoom avant"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7" />
            </svg>
          </button>
          <button
            onClick={handleReset}
            className="p-2 rounded hover:bg-gray-200 transition-colors"
            title="Réinitialiser"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
        </div>
      </div>
      
      {/* Container de l'image */}
      <div 
        className="flex-1 overflow-hidden bg-gray-50 flex items-center justify-center relative"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onWheel={handleWheel}
        style={{ cursor: zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default' }}
      >
        <img
          src={currentDoc.uri}
          alt={currentDoc.fileName || 'Document'}
          className="max-w-full max-h-full object-contain transition-transform duration-100"
          style={{
            transform: `scale(${zoom}) translate(${position.x / zoom}px, ${position.y / zoom}px)`,
          }}
          draggable={false}
        />
        
        {/* Indicateur recto/verso */}
        {totalDocs > 1 && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/60 text-white px-3 py-1 rounded-full text-sm font-medium">
            {currentIndex === 0 ? 'Recto' : currentIndex === 1 ? 'Verso' : `Image ${currentIndex + 1}`}
          </div>
        )}
      </div>
    </div>
  );
});

ImageViewerWithZoom.displayName = 'ImageViewerWithZoom';

/**
 * Composant visualiseur de documents avec skeleton de chargement
 * Mémorisé pour éviter les re-renders inutiles lors de la saisie dans les inputs
 * @param {object} props
 * @param {Array} props.docs - Liste des documents à afficher
 * @param {boolean} props.isWaiting - État de chargement
 * @param {object} props.style - Style du viewer (optionnel)
 */
const DocumentViewer = memo(({ 
  docs, 
  isWaiting, 
  style = DEFAULT_STYLE 
}) => {
  if (isWaiting) {
    return <DocumentViewerSkeleton />;
  }

  if (docs.length > 0) {
    // Vérifier si tous les documents sont des images
    const isImage = (doc) => 
      doc.fileType?.startsWith('image/') || 
      /\.(jpg|jpeg|png|gif|webp)$/i.test(doc.uri) ||
      /\.(jpg|jpeg|png|gif|webp)$/i.test(doc.fileName);

    const imageDocs = docs.filter(isImage);
    const otherDocs = docs.filter(doc => !isImage(doc));

    // Si tous les documents sont des images, utiliser le viewer personnalisé
    if (imageDocs.length > 0 && otherDocs.length === 0) {
      return (
        <div style={style} className="h-full">
          <ImageViewerWithZoom docs={imageDocs} />
        </div>
      );
    }

    // Utiliser DocViewer pour les PDFs et autres documents (ou mix)
    return (
      <DocViewer
        documents={docs}
        style={style}
        pluginRenderers={DocViewerRenderers}
      />
    );
  }

  return (
    <div className="flex justify-center items-center h-full py-3 px-1 border rounded">
      <div className="text-gray-500">Aucun document à afficher.</div>
    </div>
  );
});

DocumentViewer.displayName = 'DocumentViewer';

export default DocumentViewer;
